import { BrandSystemConfig } from "../../../remotion/Components/Theme/Brand";
import { SavedCaptionDesignSystemConfig } from "../../../remotion/Components/Captions/Caption";
import { CaptionPage } from "../../../remotion/Components/Captions/CaptionGrouper";
import { VideoCanvas, Position } from "../../../remotion/Components/DesignSystem";
import { ClassicCaptionPreview } from "../../../remotion/Components/Captions/ClassicCaptions/ClassicCaptionPreview";
import ti18n from 'i18n!nls/ClipCreatori18n'; // Import i18n

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
  }

// Preview text configurations (SSR-safe i18n)
const t = (key: string) =>
  (typeof window !== 'undefined' && window.getI18n)
    ? window.getI18n(ti18n, key)
    : // fallback to raw bundle key
      ((ti18n as any)[key] ?? key);

// Compute lazily so translations can react to locale changes
const getPreviewTexts = () => ({
  one_word: [t("PREVIEW_WORD")],
  one_line: [t("PREVIEW_ONE_LINE")],
  two_lines: [t("PREVIEW_TWO_LINES_1"), t("PREVIEW_LINE_2")],
  three_lines: [t("PREVIEW_TWO_LINES_1"), t("PREVIEW_LINE_2"), t("PREVIEW_THREE_LINES_3")]
} as const);

const PREVIEW_DURATION = 3000; // 3 seconds in milliseconds

// Map common ratios to preview font sizes (allows numeric overrides elsewhere)
const FONT_SIZE_MAP: Record<string, number> = {
  small: 36,
  medium: 48,
  large: 64,
  extra_large: 72,
};

interface CaptionTemplateProps {
    caption: SavedCaptionDesignSystemConfig;
    onClick: () => void;
    brandConfig: BrandSystemConfig;
    selected: boolean;
}

export const CaptionTemplate = ({ caption, onClick, selected, brandConfig }: CaptionTemplateProps) => {
    const PREVIEW_TEXTS = getPreviewTexts();
    
    // Create a sample caption page for preview
    const previewPage = {
        text: PREVIEW_TEXTS[caption.captionConfig.maxLines as keyof typeof PREVIEW_TEXTS].join('\n'),
        tokens: PREVIEW_TEXTS[caption.captionConfig.maxLines as keyof typeof PREVIEW_TEXTS].map((line, index) => ({
            text: line,
            fromMs: index * (PREVIEW_DURATION / PREVIEW_TEXTS[caption.captionConfig.maxLines as keyof typeof PREVIEW_TEXTS].length),
            toMs: (index + 1) * (PREVIEW_DURATION / PREVIEW_TEXTS[caption.captionConfig.maxLines as keyof typeof PREVIEW_TEXTS].length)
        })),
        startMs: 0
    } as CaptionPage;

    // Set up the preview canvas configuration
    const PREVIEW_ASPECT: VideoCanvas['aspectRatio'] = "16:9";
    const PREVIEW_WIDTH = 400;
    const PREVIEW_HEIGHT = Math.round(PREVIEW_WIDTH * 9 / 16);

    const previewCanvas: VideoCanvas = {
        width: PREVIEW_WIDTH,
        height: PREVIEW_HEIGHT,
        aspectRatio: PREVIEW_ASPECT,
        safeZone: {
            top: 0.1,
            bottom: 0.1,
            left: 0.1,
            right: 0.1
        }
    };

    return (
        <div 
            style={{outlineStyle:selected?"solid":"none"}}
            className={`cursor-pointer outline-2 outline-offset-2 outline-solid ${selected ? 'outline-[var(--trebble-primary)]' : ''}`}
            onClick={onClick}
        >
            <ClassicCaptionPreview
                page={previewPage}
                fontFamily={caption.captionConfig.font}
                canvas={previewCanvas}
                maxLines={caption.captionConfig.maxLines}
                primaryColor={caption.captionConfig.useDesignSystemColor? brandConfig.primaryColor: caption.captionConfig.mainColor}
                currentSpokenWordColor={caption.captionConfig.useDesignSystemColor? brandConfig.accentColor: caption.captionConfig.highlightColor}
                maxFontSize={
                  typeof caption.captionConfig.fontSizeRatio === 'number'
                    ? caption.captionConfig.fontSizeRatio
                    : FONT_SIZE_MAP[String(caption.captionConfig.fontSizeRatio)] ?? 48
                }
                outline={caption.captionConfig.outline}
                background={caption.captionConfig.background}
                textAnimation={caption.captionConfig.textAnimation}
                borderRadius={caption.captionConfig.borderRadius}
                captionCase={caption.captionConfig.captionCase}
                hidePunctuation={caption.captionConfig.hidePunctuation}
                position={caption.captionConfig.position as Position}
                compact={true}
            />
        </div>
    );
}; 